import { AttendanceInput, AttendanceScope, AttendeeRole, AttendeeStatus, AttendanceTrackingMode, Registration, UpdateRegistrationInput } from '../API';

export const isIndividual = (party: Registration) => party.attendanceTrackingMode === AttendanceTrackingMode.INDIVIDUAL;
export const activePeople = (party: Registration) => [...(party.attendees ?? [])]
  .filter(person => person.status === AttendeeStatus.ACTIVE)
  .sort((a, b) => Number(b.role === AttendeeRole.PRIMARY) - Number(a.role === AttendeeRole.PRIMARY)
    || a.displayOrder - b.displayOrder || a.attendeeId.localeCompare(b.attendeeId));
export function attendanceCommand(party: Registration, attendance: AttendanceInput, requestId: string): UpdateRegistrationInput {
  if (!isIndividual(party) || party.registrationStatus === 'CANCELLED' || party.registrationStatus === 'DELETED') {
    throw new Error('This party is not available for individual attendance.');
  }
  const people = activePeople(party);
  if (!requestId || !people.length) throw new Error('Refresh this party before checking in.');
  if (attendance.scope === AttendanceScope.ATTENDEE) {
    if (!people.some(p => p.attendeeId === attendance.attendeeId)) throw new Error('This attendee is no longer in the party.');
    return { eventId: party.eventId, registrationId: party.registrationId, requestId,
      attendance: { scope: AttendanceScope.ATTENDEE, checkedIn: attendance.checkedIn, attendeeId: attendance.attendeeId } };
  }
  if (!attendance.checkedIn) throw new Error('Undo check-in one person at a time.');
  if (attendance.scope === AttendanceScope.PARTY) {
    if (!Number.isInteger(party.rosterVersion) || party.rosterVersion < 1) throw new Error('Refresh the party roster first.');
    return { eventId: party.eventId, registrationId: party.registrationId, requestId,
      attendance: { scope: AttendanceScope.PARTY, checkedIn: true, expectedRosterVersion: party.rosterVersion } };
  }
  const ids = attendance.attendeeIds ?? [];
  if (attendance.scope !== AttendanceScope.SELECTED || !ids.length || new Set(ids).size !== ids.length
    || ids.some(id => !people.some(p => p.attendeeId === id))) throw new Error('Select current attendees from this party.');
  return { eventId: party.eventId, registrationId: party.registrationId, requestId,
    attendance: { scope: AttendanceScope.SELECTED, checkedIn: true, attendeeIds: [...ids] } };
}

// A receipt replay or slower read must never replace a newer party snapshot.
export function mergeParty(parties: Registration[], incoming: Registration): Registration[] {
  const previous = parties.find(p => p.eventId === incoming.eventId && p.registrationId === incoming.registrationId);
  if (previous && isIndividual(previous) && (!isIndividual(incoming) || (incoming.partyVersion ?? 0) < (previous.partyVersion ?? 0))) return parties;
  if (!previous) return [...parties, incoming];
  return parties.map(p => p === previous ? incoming : p);
}

export function scannedRegistrationId(value: string, eventId: string): string {
  try {
    const url = new URL(value);
    const parts = url.searchParams.get('q')?.split(':');
    if (!parts || parts.length !== 2 || parts[0] !== eventId || !parts[1]) throw new Error('Ticket belongs to another event or is invalid.');
    return parts[1];
  } catch (error) {
    if (/^[a-zA-Z0-9_-]+$/.test(value)) return value; // Existing tickets encode the registration ID alone.
    throw error;
  }
}

export function registrationMatchesSearch(party: Registration, search: string): boolean {
  const query = search.trim().toLowerCase();
  const people = isIndividual(party) ? activePeople(party) : [];
  return [party, ...people].some(person =>
    `${person.firstName ?? ''} ${person.lastName ?? ''}`.toLowerCase().includes(query)
    || (person.email ?? '').toLowerCase().includes(query));
}
